<template>
  <main class="account-deletion-page" aria-labelledby="account-deletion-title">
    <section class="account-deletion-card">
      <h1 id="account-deletion-title">Account Deletion Requested</h1>

      <p class="account-deletion-lead">Your account deletion request was submitted.</p>
      <p class="account-deletion-copy">
        Sign in again within 30 days to recover your account. After that, recovery is no longer possible.
      </p>

      <a class="account-deletion-callout" href="/users/sign_in">
        <span class="account-deletion-callout-title">Sign in to recover your account</span>
        <span class="account-deletion-callout-copy">You have 30 days before recovery is no longer possible.</span>
      </a>

      <form
        v-if="canShowSurvey"
        class="account-deletion-survey"
        data-test="account-deletion-survey"
        @submit.prevent="submitSurvey"
      >
        <fieldset class="feedback" :disabled="submitting">
          <legend class="feedback-prompt">What made you decide to leave?</legend>
          <p class="feedback-support">Select all that apply. This helps us improve, and you can skip it.</p>

          <div class="reason-list">
            <div v-for="reason in reasons" :key="reason.value" class="reason">
              <button
                class="reason-card"
                type="button"
                :class="{ 'reason-card--selected': isSelected(reason.value) }"
                :aria-pressed="isSelected(reason.value).toString()"
                :data-test="`exit-interview-${reason.value}`"
                @click="toggleReason(reason.value)"
              >
                <span class="reason-check" aria-hidden="true"></span>
                <span class="reason-content">
                  <span class="reason-text">{{ reason.label }}</span>
                  <span class="reason-hint">{{ reason.hint }}</span>
                </span>
              </button>
              <textarea
                v-if="acceptsDetails(reason.value)"
                v-model="details[reason.value]"
                class="reason-details"
                maxlength="1000"
                :aria-label="`${reason.label} details`"
                placeholder="Anything specific? (optional)"
                @input="scheduleDraftSave"
              ></textarea>
            </div>
          </div>
        </fieldset>

        <p v-if="errorMessage" class="survey-error" role="alert">{{ errorMessage }}</p>

        <button class="survey-submit" type="submit" data-test="submit-exit-survey" :disabled="submitting">
          {{ submitting ? 'Submitting...' : 'Submit feedback' }}
        </button>
      </form>

      <p v-if="submitted" class="survey-success" data-test="exit-survey-success">
        Thank you for the feedback. It helps us improve Spireworks.
      </p>
    </section>
  </main>
</template>

<script>
import axios from '../../axios-json-csrf';

const EXIT_INTERVIEW_OTHER_REASON = 'other';
const DRAFT_SAVE_DELAY_MS = 400;

const EXIT_INTERVIEW_REASONS = [
  {
    value: 'user_experience',
    label: 'User experience',
    hint: 'Navigation, controls, or overall experience',
  },
  {
    value: 'customer_support',
    label: 'Customer support',
    hint: 'Response time, support quality, or follow-up',
  },
  {
    value: 'technical_issues',
    label: 'Technical issues',
    hint: 'Bugs, performance, or reliability',
  },
  {
    value: 'did_not_like_app',
    label: "Didn't like the app",
    hint: 'Features, value, or product fit',
  },
  {
    value: 'did_not_use_enough',
    label: "Didn't use it enough",
    hint: 'Not enough need or opportunities to use it',
  },
  {
    value: EXIT_INTERVIEW_OTHER_REASON,
    label: 'Other',
    hint: 'Tell us what happened',
  },
];

export default {
  data() {
    return {
      selectedReasons: [],
      details: {},
      submitting: false,
      submitted: false,
      errorMessage: '',
      reasons: EXIT_INTERVIEW_REASONS,
      draftSaveTimeout: null,
      draftSavePromise: Promise.resolve(),
    };
  },
  beforeDestroy() {
    this.clearDraftSaveTimeout();
  },
  computed: {
    feedbackToken() {
      return this.$route?.query?.feedback_token || '';
    },
    canShowSurvey() {
      return Boolean(this.feedbackToken) && !this.submitted;
    },
  },
  methods: {
    isSelected(reason) {
      return this.selectedReasons.includes(reason);
    },
    toggleReason(reason) {
      this.clearDraftSaveTimeout();

      if (this.isSelected(reason)) {
        this.selectedReasons = this.selectedReasons.filter(selectedReason => selectedReason !== reason);
        this.saveDraft();
        return;
      }

      this.selectedReasons = [...this.selectedReasons, reason];
      this.saveDraft();
    },
    acceptsDetails(reason) {
      return reason === EXIT_INTERVIEW_OTHER_REASON && this.isSelected(reason);
    },
    exitInterviewPayload() {
      return {
        skipped: this.selectedReasons.length === 0,
        reasons: this.selectedReasons.map(reason => ({
          reason,
          details: this.details[reason] || '',
        })),
      };
    },
    clearDraftSaveTimeout() {
      clearTimeout(this.draftSaveTimeout);
      this.draftSaveTimeout = null;
    },
    scheduleDraftSave() {
      this.clearDraftSaveTimeout();
      this.draftSaveTimeout = setTimeout(() => {
        this.draftSaveTimeout = null;
        this.saveDraft();
      }, DRAFT_SAVE_DELAY_MS);
    },
    saveDraft() {
      if (!this.canShowSurvey) return Promise.resolve();

      this.draftSavePromise = this.draftSavePromise
        .catch(() => {})
        .then(() =>
          axios.patch('/users/account_deletion_feedback', {
            feedback_token: this.feedbackToken,
            exit_interview: this.exitInterviewPayload(),
          })
        );

      return this.draftSavePromise.catch(() => {});
    },
    flushDraftSave() {
      this.clearDraftSaveTimeout();
      return this.saveDraft();
    },
    submitSurvey() {
      this.submitting = true;
      this.errorMessage = '';

      return this.flushDraftSave()
        .then(() =>
          axios.post('/users/account_deletion_feedback', {
            feedback_token: this.feedbackToken,
            exit_interview: this.exitInterviewPayload(),
          })
        )
        .then(() => {
          this.submitted = true;
        })
        .catch(() => {
          this.errorMessage = 'Something went wrong while submitting your feedback. Please try again.';
        })
        .finally(() => {
          this.submitting = false;
        });
    },
  },
};
</script>

<style scoped>
.account-deletion-page {
  display: flex;
  min-height: 100vh;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background:
    linear-gradient(rgba(4, 8, 26, 0.76), rgba(4, 8, 26, 0.92)),
    url('/images/auth-background.jpg') center / cover no-repeat;
  color: #ffffff;
  font-family: Graphik, system-ui, sans-serif;
}

.account-deletion-card {
  width: min(100%, 680px);
  padding: 3rem;
  background-color: #1d2236;
  border: 1px solid rgba(132, 129, 255, 0.35);
  border-radius: 6px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.5);
}

.account-deletion-card h1 {
  margin: 0 0 2rem;
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: 400;
  line-height: 1.12;
}

.account-deletion-lead {
  margin: 0 0 1.25rem;
  font-size: 1.1rem;
  font-weight: 600;
}

.account-deletion-copy {
  margin: 0 0 2rem;
  color: rgba(255, 255, 255, 0.78);
  font-size: 1.1rem;
  line-height: 1.5;
}

.account-deletion-callout {
  display: grid;
  gap: 0.4rem;
  padding: 1.25rem 1.5rem;
  background-color: rgba(82, 84, 255, 0.18);
  border: 1px solid #5254ff;
  border-radius: 6px;
  color: #ffffff;
  text-decoration: none;
  transition:
    background-color 150ms ease,
    border-color 150ms ease,
    transform 100ms ease;
}

.account-deletion-callout:hover {
  background-color: rgba(82, 84, 255, 0.28);
  border-color: #8481ff;
}

.account-deletion-callout:active {
  transform: translateY(1px);
}

.account-deletion-callout:focus-visible {
  outline: 2px solid #8481ff;
  outline-offset: 3px;
}

.account-deletion-callout-title {
  font-size: 1.2rem;
  font-weight: 600;
}

.account-deletion-callout-copy {
  color: rgba(255, 255, 255, 0.74);
  font-size: 0.95rem;
  line-height: 1.4;
}

.account-deletion-survey {
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid rgba(132, 129, 255, 0.28);
}

.feedback {
  margin: 0;
  padding: 0;
  border: 0;
}

.feedback:disabled {
  opacity: 0.72;
}

.feedback-prompt {
  margin-bottom: 0.35rem;
  padding: 0;
  font-size: 1.35rem;
  font-weight: 600;
  color: #ffffff;
}

.feedback-support {
  margin: 0 0 1rem;
  color: rgba(255, 255, 255, 0.68);
  font-size: 0.95rem;
  line-height: 1.4;
}

.reason-list {
  display: grid;
  gap: 0.75rem;
}

.reason-card {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  width: 100%;
  margin: 0;
  padding: 0.85rem 1rem;
  background-color: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(132, 129, 255, 0.35);
  border-radius: 6px;
  color: inherit;
  cursor: pointer;
  text-align: left;
  user-select: none;
  transition:
    background-color 150ms ease,
    border-color 150ms ease;
}

.reason-card:hover,
.reason-card--selected {
  background-color: rgba(82, 84, 255, 0.12);
  border-color: #8481ff;
}

.reason-card:focus-visible,
.survey-submit:focus-visible {
  outline: 2px solid #8481ff;
  outline-offset: 2px;
}

.reason-check {
  position: relative;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  margin-top: 0.1rem;
  border: 1px solid rgba(255, 255, 255, 0.45);
  border-radius: 4px;
}

.reason-card--selected .reason-check {
  background-color: #5254ff;
  border-color: #5254ff;
}

.reason-card--selected .reason-check::after {
  position: absolute;
  top: 2px;
  left: 6px;
  width: 6px;
  height: 11px;
  border: solid #ffffff;
  border-width: 0 2px 2px 0;
  content: '';
  transform: rotate(45deg);
}

.reason-content {
  display: grid;
  gap: 0.25rem;
}

.reason-text {
  color: #ffffff;
}

.reason-hint {
  color: rgba(255, 255, 255, 0.62);
  font-size: 0.85rem;
}

.reason-details {
  display: block;
  width: 100%;
  min-height: 70px;
  margin-top: 0.5rem;
  padding: 0.5rem 0.75rem;
  background-color: rgba(0, 0, 0, 0.18);
  border: 1px solid rgba(132, 129, 255, 0.35);
  border-radius: 6px;
  color: #ffffff;
  resize: vertical;
}

.reason-details::placeholder {
  color: rgba(255, 255, 255, 0.35);
}

.reason-details:focus {
  outline: none;
  border-color: #8481ff;
}

.survey-submit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 1rem;
  padding: 0.85rem 1.25rem;
  background-color: #5254ff;
  border: 1px solid #5254ff;
  border-radius: 6px;
  color: #ffffff;
  font-weight: 600;
  cursor: pointer;
  transition:
    background-color 150ms ease,
    border-color 150ms ease,
    filter 150ms ease;
}

.survey-submit:hover {
  filter: brightness(1.08);
}

.survey-submit:disabled {
  cursor: not-allowed;
  opacity: 0.72;
}

.survey-error {
  margin: 1rem 0 0;
  color: #ff5a5f;
  font-size: 0.95rem;
}

.survey-success {
  margin: 2rem 0 0;
  padding: 1rem 1.25rem;
  background-color: rgba(82, 84, 255, 0.18);
  border: 1px solid #5254ff;
  border-radius: 6px;
  color: #ffffff;
  font-weight: 600;
}

@media (max-width: 480px) {
  .account-deletion-page {
    align-items: flex-start;
    padding: 1rem;
  }

  .account-deletion-card {
    padding: 2rem 1.25rem;
  }
}
</style>
